/**
 * CR-8197: Communications tab of the document details pop-up.
 *
 * The document's owner holds a private conversation with each company that can
 * see it. The owner (and Helix, read-only) pick the conversation from a list;
 * everyone else only ever has their own, which the server enforces. Chat layout
 * follows pages/supplier-detail/ChatPanel.tsx.
 */
import { useState, useEffect, useRef, useCallback } from 'react';
import { documentsAPI, type DocumentConversations, type DocumentMessage } from '../../lib/api';
import { useFormatters } from '../../contexts/PreferencesContext';
import { Button } from '../../components/ui/button';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import { Loader2, Send, MessageSquare, FileUp, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import { getApiErrorMessage } from '../../utils/errorUtils';

/** Longest message the server accepts. */
const MAX_MESSAGE_LENGTH = 2000;

interface CommunicationsTabProps {
  documentId: number;
  /** From useDocumentConversations — loaded when the pop-up opens. */
  summary: DocumentConversations | null;
  summaryLoading: boolean;
  summaryError: string | null;
  onRetrySummary: () => void;
  /** Conversation to open first (deep link). Only the owner/Helix have a choice. */
  initialCounterpart?: number | null;
  /** True while the tab is showing; messages are only marked read then. */
  active: boolean;
  /** Called once a conversation has been marked read on the server. */
  onConversationRead: (companyId: number) => void;
  /** Bumped by the pop-up when something outside the tab (a version upload
   *  with notes) may have added entries, so the open conversation reloads. */
  refreshKey?: number;
}

export default function CommunicationsTab({
  documentId, summary, summaryLoading, summaryError, onRetrySummary,
  initialCounterpart, active, onConversationRead, refreshKey = 0,
}: CommunicationsTabProps) {
  const { formatDateTime } = useFormatters();
  const [selected, setSelected] = useState<number | null>(null);
  const [messages, setMessages] = useState<DocumentMessage[]>([]);
  // Which conversation `messages` belongs to — read is only marked once the
  // shown conversation's messages have actually loaded.
  const [loadedFor, setLoadedFor] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const endRef = useRef<HTMLDivElement>(null);
  // Guards against a slow response for a conversation the user has already
  // switched away from overwriting the one now selected.
  const requestRef = useRef(0);

  const conversations = summary?.conversations ?? [];
  const hasPicker = !!summary && summary.side !== 'counterpart';
  // A counterpart never names a conversation: the server forces its own.
  const counterpartParam = hasPicker ? selected ?? undefined : undefined;
  const unread = conversations.find((c) => c.company_id === selected)?.unread_count ?? 0;

  // Pick the conversation once the list arrives: keep the current one, else
  // the deep-linked one, else the most recently active (the list's order).
  useEffect(() => {
    if (!summary) return;
    const ids = summary.conversations.map((c) => c.company_id);
    setSelected((current) => {
      if (current !== null && ids.includes(current)) return current;
      if (initialCounterpart && ids.includes(initialCounterpart)) return initialCounterpart;
      return ids[0] ?? null;
    });
  }, [summary, initialCounterpart]);

  const loadMessages = useCallback(async () => {
    if (selected === null) return;
    const request = ++requestRef.current;
    setLoading(true);
    setLoadError(null);
    try {
      const res = await documentsAPI.getDocumentMessages(documentId, counterpartParam);
      if (request !== requestRef.current) return;
      setMessages(res.data);
      setLoadedFor(selected);
    } catch (err: unknown) {
      if (request !== requestRef.current) return;
      console.error('Failed to load document messages', err);
      setLoadError(getApiErrorMessage(err, 'Couldn’t load messages'));
    } finally {
      if (request === requestRef.current) setLoading(false);
    }
  }, [documentId, selected, counterpartParam]);

  useEffect(() => {
    if (active) loadMessages();
  }, [active, loadMessages, refreshKey]);

  // Opening the tab on a conversation clears its unread count — once its
  // messages are actually on screen, not before or after a failed load.
  const shown = loadedFor !== null && loadedFor === selected && !loading && !loadError;
  useEffect(() => {
    if (!active || !shown || selected === null || unread === 0 || summary?.side === 'observer') return;
    documentsAPI.markDocumentMessagesRead(documentId, counterpartParam)
      .then(() => onConversationRead(selected))
      .catch((err: unknown) => console.error('Failed to mark document messages read', err));
  }, [active, shown, selected, unread, summary?.side, documentId, counterpartParam, onConversationRead]);

  useEffect(() => {
    endRef.current?.scrollIntoView?.({ block: 'end' });
  }, [messages]);

  const send = async () => {
    const text = draft.trim();
    if (!text || sending) return;
    setSending(true);
    try {
      const res = await documentsAPI.postDocumentMessage(documentId, text, counterpartParam);
      setMessages((prev) => [...prev, res.data]);
      setDraft('');
    } catch (err: unknown) {
      // The draft is kept so the user can resend it.
      console.error('Failed to send document message', err);
      toast.error(getApiErrorMessage(err, 'Failed to send message'));
    } finally {
      setSending(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  if (summaryError) {
    return (
      <div className="text-center py-10 text-muted-foreground text-sm">
        <AlertCircle className="h-8 w-8 mx-auto mb-2 opacity-50" />
        <p>{summaryError}</p>
        <Button variant="outline" size="sm" className="mt-3" onClick={onRetrySummary}>Try again</Button>
      </div>
    );
  }

  if (summaryLoading || !summary) {
    return (
      <div className="flex items-center justify-center py-10">
        <Loader2 className="h-6 w-6 animate-spin" aria-label="Loading conversations" />
      </div>
    );
  }

  // Someone who can open the document only because they uploaded it, while
  // acting as a company it isn't shared with, has no conversation to join.
  if (!hasPicker && conversations.length === 0) {
    return (
      <div className="text-center py-10 text-muted-foreground text-sm">
        <MessageSquare className="h-8 w-8 mx-auto mb-2 opacity-50" />
        <p>This document isn’t shared with your company, so there’s no conversation to join.</p>
      </div>
    );
  }

  if (hasPicker && conversations.length === 0) {
    return (
      <div className="text-center py-10 text-muted-foreground text-sm">
        <MessageSquare className="h-8 w-8 mx-auto mb-2 opacity-50" />
        <p>No conversations yet.</p>
        <p className="mt-1">A conversation starts when this document is shared with a company, or when a company that can see it writes about it.</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      {hasPicker && (
        <Select value={selected !== null ? String(selected) : ''} onValueChange={(v) => setSelected(Number(v))}>
          <SelectTrigger aria-label="Conversation with">
            <SelectValue placeholder="Choose a company" />
          </SelectTrigger>
          <SelectContent>
            {conversations.map((c) => (
              <SelectItem key={c.company_id} value={String(c.company_id)}>
                <span className="flex items-center gap-2">
                  {c.company_name}
                  {c.unread_count > 0 && <Badge variant="destructive" className="h-5 px-1.5 text-xs">{c.unread_count}</Badge>}
                </span>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}

      <div className="min-h-[240px] max-h-[420px] overflow-y-auto space-y-3 rounded-md border p-3">
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-5 w-5 animate-spin" aria-label="Loading messages" />
          </div>
        ) : loadError ? (
          <div className="text-center py-8 text-muted-foreground text-sm">
            <p>{loadError}</p>
            <Button variant="outline" size="sm" className="mt-3" onClick={loadMessages}>Try again</Button>
          </div>
        ) : messages.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground text-sm">
            <MessageSquare className="h-8 w-8 mx-auto mb-2 opacity-50" />
            <p>No messages yet</p>
          </div>
        ) : (
          messages.map((msg) => (
            msg.kind === 'version_note' ? (
              <div key={msg.id} className="flex flex-col items-center text-center">
                <div className="flex items-start gap-2 rounded-md bg-muted/60 px-3 py-2 text-sm text-muted-foreground max-w-[90%]">
                  <FileUp className="h-4 w-4 mt-0.5 shrink-0" />
                  <span className="whitespace-pre-wrap break-words">{msg.text}</span>
                </div>
                <span className="mt-1 text-[11px] text-muted-foreground">
                  {msg.sender_name} · {msg.sender_company_name} · {formatDateTime(msg.created_at)}
                </span>
              </div>
            ) : (
              <div key={msg.id} className={`flex flex-col ${msg.is_own ? 'items-end' : 'items-start'}`}>
                <div className="mb-0.5 flex flex-wrap items-center gap-1.5 text-xs">
                  <span className="font-medium">{msg.sender_name}{msg.is_own ? ' (you)' : ''}</span>
                  <span className="text-muted-foreground">{msg.sender_company_name}</span>
                  <span className="text-[11px] text-muted-foreground">{formatDateTime(msg.created_at)}</span>
                </div>
                <div className={`max-w-[85%] whitespace-pre-wrap break-words rounded-lg px-3 py-2 text-sm ${
                  msg.is_own ? 'bg-primary text-primary-foreground' : 'bg-muted'
                }`}>
                  {msg.text}
                </div>
              </div>
            )
          ))
        )}
        <div ref={endRef} />
      </div>

      {summary.can_post ? (
        <div>
          <Textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Write a message… (Enter to send, Shift+Enter for a new line)"
            aria-label="Message"
            rows={2}
            maxLength={MAX_MESSAGE_LENGTH}
            disabled={selected === null}
          />
          <div className="mt-2 flex justify-end">
            <Button size="sm" onClick={send} disabled={!draft.trim() || sending || selected === null}>
              {sending ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Send className="mr-1 h-4 w-4" />}
              Send
            </Button>
          </div>
        </div>
      ) : (
        <p className="text-xs text-muted-foreground">Read only: Helix users can read document conversations but not post in them.</p>
      )}
    </div>
  );
}
